import React, { Component } from 'react';
import styled from 'styled-components';
import { media } from '../../styles/style-utils';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import Button from '../common/Button';
import Hyperlink from '../common/Hyperlink';
import CookieBannerSelection from './CookieBannerSelection';
import { loadSegment, getAnonymousId } from '../../analytics';
import { COOKIE_PREFERENCES_COOKIE } from '../../constants';
import { withRouteData, withSiteData } from 'react-static';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';

const MODAL_BLACKLIST_PATHS = ['privacy', 'terms', 'imprint', 'cookie-policy'];

const Modal = styled(ReactModal)`
  overflow-x: hidden;
  overflow-y: auto;
`;

const Banner = styled.div`
  background-color: var(--c-charcoal);
  bottom: 0;
  font-size: var(--fs-focus);
  left: 0;
  padding: var(--fr);
  position: fixed;
  text-align: center;
  width: 100%;
  z-index: 2000; /* render on top of side bar and player bar */
  ${media.mediumLarge`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: calc(var(--fr) * 1.5);
    text-align: left;
  `};
  a {
    text-decoration: underline;
  }

  p {
    font-size: var(--fs-focus);
  }

  .inapp-webview & {
    display: none;
  }
  .ios-webview & {
    display: none;
  }
`;

const BannerInner = styled.div`
  max-width: 72rem;
  margin: 0 auto;
`;

const SelectionWrapper = styled.div`
  ${media.mediumLarge`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `};
`;

const SelectionOptions = styled.div`
  margin-bottom: 2rem;
  ${media.mediumLarge`
    margin-bottom: 1rem;
    flex: 1;
  `};
`;

const SeeDetailsWrapper = styled.div`
  text-transform: uppercase;
  margin-left: 2px;
  padding-top: 1rem;
  white-space: nowrap;
  ${media.mediumLarge`
    margin-right: 16px;
  `}
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: var(--fr);

  button {
    flex: 1;
    ${media.mediumLarge`
      flex: initial;
      min-width: 13rem;
    `}
  }

  ${media.small`
    flex-direction: row;
  `}
`;

class CookieBanner extends Component {
  state = {
    visible: false,
    preferences: {
      necessary: true,
      marketingAndAnalytics: false,
      advertising: false,
    },
  };

  constructor(props) {
    super();
    this.isInApp = Cookies.get('ctc') === 'inapp-webview';
    this.useModal =
      props.pathWithoutBasename &&
      !MODAL_BLACKLIST_PATHS.some(keyword => props.pathWithoutBasename.includes(keyword));
  }

  componentDidMount() {
    if (!Boolean(Cookies.get(COOKIE_PREFERENCES_COOKIE)) && !this.isInApp) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ visible: true });
      this.trackBannerImpression();
    }

    if (this.isInApp) {
      this.onAcceptAllCookies();
    }
  }

  onAcceptAllCookies = () => {
    this.saveConsent({
      necessary: true,
      marketingAndAnalytics: true,
      advertising: true,
    });
    this.hideBanner();
  };

  onAllowSelection = () => {
    this.saveConsent(this.state.preferences);
    this.hideBanner();
  };

  onSelectionChange = preferences => {
    this.setState({ preferences });
  };

  render() {
    if (!this.state.visible) {
      return null;
    }

    const Wrapper = this.useModal ? Modal : React.Fragment;

    return (
      <Wrapper
        onRequestClose={this.hideBanner}
        isOpen={this.state.visible}
        bodyOpenClassName="modalOpen"
        overlayClassName="overlay"
      >
        <Banner>
          <BannerInner>
            <div>
              <p>
                <FormattedMessage
                  defaultMessage="We use cookies to recognise your repeat visits, enable product functionality, and improve our marketing. This includes cookies that are necessary for the operation of the site, as well as cookies that are used only for anonymous statistical purposes, for storing your personal settings or to display personalized content. You can decide which categories you want to allow. Please note that based on your settings, not all functionalities of the site may be available."
                  id="cookie-banner.text.info-1"
                />
              </p>
              <p>
                <FormattedMessage
                  defaultMessage="Further information can be found in our {privacyPolicyLink}."
                  id="cookie-banner.text.info-2"
                  values={{
                    privacyPolicyLink: (
                      <Hyperlink to="/privacy" openInNewTab>
                        <FormattedMessage
                          id="cookie-banner.privacy-policy-link-small.text"
                          defaultMessage="privacy policy"
                        />
                      </Hyperlink>
                    ),
                  }}
                />
              </p>
            </div>
            <SelectionWrapper>
              <SelectionOptions>
                <CookieBannerSelection
                  selection={this.state.preferences}
                  onChange={this.onSelectionChange}
                />
                <SeeDetailsWrapper>
                  <Hyperlink to={`${this.props.appDomain}/consent-manager`} openInNewTab>
                    <FormattedMessage id="cookie-banner.see-details" defaultMessage="See Details" />
                  </Hyperlink>
                </SeeDetailsWrapper>
              </SelectionOptions>
              <ButtonsWrapper>
                <Button color="white" onClick={this.onAllowSelection}>
                  <FormattedMessage
                    defaultMessage="Allow Selection"
                    id="cookie-banner.allow-selection"
                  />
                </Button>
                <Button color="red" onClick={this.onAcceptAllCookies}>
                  <FormattedMessage
                    defaultMessage="Allow All Cookies"
                    id="cookie-banner.allow-all-cookies"
                  />
                </Button>
              </ButtonsWrapper>
            </SelectionWrapper>
          </BannerInner>
        </Banner>
      </Wrapper>
    );
  }

  hideBanner() {
    this.setState({ visible: false });
  }

  trackBannerImpression = () => {
    this.sendBannerBeacon('/api/cookie-banner-impression');
  };

  trackBannerAccepted = preferences => {
    this.sendBannerBeacon('/api/cookie-banner-accepted', preferences);
  };

  sendBannerBeacon = (endpoint, properties) => {
    const params = new URLSearchParams({
      ...properties,
      anonymousId: getAnonymousId(),
    });

    try {
      navigator.sendBeacon(`${endpoint}?${params.toString()}`);
    } catch (e) {
      // eslint-disable-next-line
      console.error('Failed to send beacon', e);
    }
  };

  saveConsent = preferences => {
    if (!Cookies.get(COOKIE_PREFERENCES_COOKIE)) {
      Cookies.set(COOKIE_PREFERENCES_COOKIE, preferences, {
        expires: 365 * 100,
        domain: process.env.TOP_DOMAIN,
      });
    }

    if (!this.isInApp) {
      this.trackBannerAccepted(preferences);
    }

    if (typeof document !== 'undefined' && window.analytics) {
      loadSegment(preferences);
    }
  };
}

CookieBanner.propTypes = {
  pathWithoutBasename: PropTypes.string.isRequired,
  appDomain: PropTypes.string.isRequired,
};

export default withSiteData(withRouteData(CookieBanner));



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/cookieBanner/CookieBanner.js